/**
 * The OPEN LIST — what the sidebar's Home view shows.
 *
 * The canvas holds one file at a time (RFC 0266 §3a), so "open" here means what
 * it means in an editor's Open Editors section: the things you have reached for
 * in this project, most recent first, with the current one highlighted. It is
 * the sidebar's default view because a project's working set is a handful of
 * items, while its full directory is everything anyone ever made.
 *
 * Only IDS are stored. Titles, paths and mimes are resolved against the live
 * catalog at render, so a renamed document shows its new name and a deleted one
 * disappears instead of leaving a row that opens nothing.
 *
 * PER SPACE, in localStorage, beside useLayout's blob rather than inside it
 * (RFC 0281 §7). It used to be keyed by project, one flat list for everything
 * you touched. Scoped to a space it answers a better question: not what you
 * opened, but what you opened WHILE DOING THIS.
 *
 * It stays on the device and stays capped, because it is a convenience over a
 * durable tree rather than a record of anything — the space's own tree is what
 * holds work, and this is only the tail of what fell out of it.
 *
 * The prefix took a new version because the key changed MEANING. Reading a
 * project-keyed list as a space's would show you the wrong recents under a
 * name that looked right, which is worse than starting empty.
 */

import { useCallback, useEffect, useState } from 'react'

const PREFIX = 'mod_w_open_v2:'
/** Enough to cover a working set; past this, the tail is not a working set. */
const CAP = 24

const keyFor = (spaceId: string) => `${PREFIX}${spaceId}`

/** A space's Open list as stored, for a reader outside the hook (RFC 0330). */
export function load(spaceId: string): string[] {
  try {
    const raw = localStorage.getItem(keyFor(spaceId))
    if (!raw) return []
    const parsed = JSON.parse(raw) as unknown
    if (!Array.isArray(parsed)) return []
    return parsed
      .filter((v): v is string => typeof v === 'string')
      .slice(0, CAP)
  } catch {
    return []
  }
}

/**
 * Put an id at the top of ANOTHER space's Open list, in storage (RFC 0336
 * S10). For a put-through that moves the window: the hook only ever holds the
 * space on screen, and the switch lands a render later, so a note through it
 * would file the session under the space being left. Written straight to the
 * destination's key, it is what that space loads when the switch arrives.
 */
export function noteOpenedIn(spaceId: string, id: string): void {
  if (!spaceId) return
  try {
    localStorage.setItem(
      keyFor(spaceId),
      JSON.stringify(withOpened(load(spaceId), id)),
    )
  } catch {
    // best-effort persistence, as the hook's own
  }
}

/** Most-recent-first, deduped, capped. Pure so the ordering is testable. */
export function withOpened(ids: string[], id: string): string[] {
  return [id, ...ids.filter((existing) => existing !== id)].slice(0, CAP)
}

export function useOpenItems(spaceId: string) {
  const [ids, setIds] = useState<string[]>(() => load(spaceId))

  // Switching SPACES re-reads rather than carrying the previous list over:
  // the recents of the space you left are not the recents of the one you
  // entered, and showing them would be the flat list this replaced.
  useEffect(() => setIds(load(spaceId)), [spaceId])

  useEffect(() => {
    try {
      localStorage.setItem(keyFor(spaceId), JSON.stringify(ids))
    } catch {
      // best-effort persistence
    }
  }, [spaceId, ids])

  const noteOpened = useCallback((id: string) => {
    setIds((prev) => (prev[0] === id ? prev : withOpened(prev, id)))
  }, [])

  const removeOpened = useCallback((id: string) => {
    setIds((prev) => prev.filter((existing) => existing !== id))
  }, [])

  return { openIds: ids, noteOpened, removeOpened }
}
